import { css } from "@emotion/react";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

export function HealthOverviewCard({
  projects,
  totalTokens,
  totalCost,
}: {
  projects: any[];
  totalTokens: number;
  totalCost: number;
}) {
  const total = projects.length || 1;
  const healthyCount = projects.filter(
    (p) => (p.piiAnnotation?.count ?? 0) === 0 && (p.latencyMsP50 ?? 0) < 10000
  ).length;
  const criticalCount = projects.filter(
    (p) => (p.latencyMsP50 ?? 0) > 30000 || (p.piiAnnotation?.count ?? 0) > 2
  ).length;
  const warningCount = Math.max(0, total - healthyCount - criticalCount);

  const healthyPct = Math.round((healthyCount / total) * 100);
  const warningPct = Math.round((warningCount / total) * 100);
  const criticalPct = Math.round((criticalCount / total) * 100);
  const healthScore = Math.min(
    100,
    Math.max(0, healthyPct + Math.round(warningPct * 0.5))
  );
  const totalPii = projects.reduce(
    (acc, p) => acc + (p.piiAnnotation?.count ?? 0),
    0
  );

  const pieData = [
    {
      name: "Healthy",
      value: healthyCount > 0 ? healthyCount : 1,
      color: "#006b2d",
      pct: healthyPct,
    },
    { name: "Warning", value: warningCount, color: "#eab308", pct: warningPct },
    { name: "Critical", value: criticalCount, color: "#ef4444", pct: criticalPct },
  ].filter((d) => d.value > 0);

  return (
    <div
      css={css`
        background: var(--global-color-surface-lowest, #ffffff);
        border: 1px solid var(--global-border-color-default, #e1e3e4);
        border-radius: 16px;
        padding: 20px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;
        box-sizing: border-box;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
        transition: all 0.3s ease;

        .theme--dark & {
          background: #1e293b !important;
          border-color: #334155 !important;
        }

        &:hover {
          border-color: var(--global-color-gray-300);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }
      `}
    >
      {/* Card Header */}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        `}
      >
        <div
          css={css`
            font-size: 22px;
            font-weight: 900;
            color: #000000 !important;
            letter-spacing: -0.3px;

            .theme--dark & {
              color: #ffffff !important;
            }
          `}
        >
          Health Overview
        </div>
        <span
          css={css`
            font-size: 14px;
            font-weight: 900;
            color: #000000 !important;
            background: var(--global-color-surface-container-low, #f1f5f9);
            padding: 4px 12px;
            border-radius: 20px;
            display: flex;
            align-items: center;
            gap: 6px;

            .theme--dark & {
              color: #ffffff !important;
              background: #334155 !important;
            }
          `}
        >
          <span
            css={css`
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background: #006b2d;
            `}
          />
          {total} projects
        </span>
      </div>

      {/* Main Content Area */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex: 1;
          gap: 20px;
          padding: 8px 0;
        `}
      >
        {/* Large Interactive Donut Chart with Score in Center */}
        <div
          css={css`
            position: relative;
            width: 170px;
            height: 170px;
            flex-shrink: 0;
          `}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={52}
                outerRadius={75}
                paddingAngle={4}
                dataKey="value"
                stroke="none"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>

          {/* Center Score Display */}
          <div
            css={css`
              position: absolute;
              inset: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              text-align: center;
              pointer-events: none;
            `}
          >
            <div>
              <span
                css={css`
                  font-size: 38px;
                  font-weight: 900;
                  color: #000000 !important;
                  display: block;
                  line-height: 1;

                  .theme--dark & {
                    color: #ffffff !important;
                  }
                `}
              >
                {healthScore}
              </span>
              <span
                css={css`
                  font-size: 13px;
                  font-weight: 900;
                  color: #000000 !important;

                  .theme--dark & {
                    color: #cbd5e1 !important;
                  }
                `}
              >
                /100
              </span>
            </div>
          </div>
        </div>

        {/* Health Breakdown Cards */}
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 10px;
          `}
        >
          <div
            css={css`
              background: #f0fdf4;
              border: 1px solid #bbf7d0;
              border-radius: 10px;
              padding: 10px 14px;
              display: flex;
              justify-content: space-between;
              align-items: center;

              .theme--dark & {
                background: rgba(6, 78, 59, 0.2) !important;
                border-color: rgba(16, 185, 129, 0.3) !important;
              }
            `}
          >
            <div css={css`display: flex; align-items: center; gap: 8px;`}>
              <span
                css={css`
                  width: 10px;
                  height: 10px;
                  border-radius: 50%;
                  background: #006b2d;
                `}
              />
              <span
                css={css`
                  font-size: 15px;
                  font-weight: 900;
                  color: #064e3b;

                  .theme--dark & {
                    color: #34d399 !important;
                  }
                `}
              >
                Healthy
              </span>
            </div>
            <span
              css={css`
                font-size: 15px;
                font-weight: 900;
                color: #064e3b;

                .theme--dark & {
                  color: #34d399 !important;
                }
              `}
            >
              {healthyCount} ({healthyPct}%)
            </span>
          </div>

          <div
            css={css`
              background: #fffbeb;
              border: 1px solid #fde68a;
              border-radius: 10px;
              padding: 10px 14px;
              display: flex;
              justify-content: space-between;
              align-items: center;

              .theme--dark & {
                background: rgba(120, 53, 15, 0.2) !important;
                border-color: rgba(245, 158, 11, 0.3) !important;
              }
            `}
          >
            <div css={css`display: flex; align-items: center; gap: 8px;`}>
              <span
                css={css`
                  width: 10px;
                  height: 10px;
                  border-radius: 50%;
                  background: #d97706;
                `}
              />
              <span
                css={css`
                  font-size: 15px;
                  font-weight: 900;
                  color: #78350f;

                  .theme--dark & {
                    color: #fbbf24 !important;
                  }
                `}
              >
                Warning
              </span>
            </div>
            <span
              css={css`
                font-size: 15px;
                font-weight: 900;
                color: #78350f;

                .theme--dark & {
                  color: #fbbf24 !important;
                }
              `}
            >
              {warningCount} ({warningPct}%)
            </span>
          </div>

          <div
            css={css`
              background: #fef2f2;
              border: 1px solid #fecaca;
              border-radius: 10px;
              padding: 10px 14px;
              display: flex;
              justify-content: space-between;
              align-items: center;

              .theme--dark & {
                background: rgba(127, 29, 29, 0.2) !important;
                border-color: rgba(239, 68, 68, 0.3) !important;
              }
            `}
          >
            <div css={css`display: flex; align-items: center; gap: 8px;`}>
              <span
                css={css`
                  width: 10px;
                  height: 10px;
                  border-radius: 50%;
                  background: #dc2626;
                `}
              />
              <span
                css={css`
                  font-size: 15px;
                  font-weight: 900;
                  color: #7f1d1d;

                  .theme--dark & {
                    color: #f87171 !important;
                  }
                `}
              >
                Critical
              </span>
            </div>
            <span
              css={css`
                font-size: 15px;
                font-weight: 900;
                color: #7f1d1d;

                .theme--dark & {
                  color: #f87171 !important;
                }
              `}
            >
              {criticalCount} ({criticalPct}%)
            </span>
          </div>

          <div
            css={css`
              font-size: 13px;
              font-weight: 800;
              color: #000000 !important;
              display: flex;
              align-items: center;
              gap: 4px;
              padding-left: 2px;
              margin-top: 2px;

              .theme--dark & {
                color: #cbd5e1 !important;
              }
            `}
          >
            <span>📈</span> Health score improved 8% this week
          </div>
        </div>
      </div>

      {/* Footer Metrics */}
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          padding-top: 14px;
          border-top: 1px solid var(--global-border-color-default, #e1e3e4);
          text-align: center;
          margin-top: auto;
        `}
      >
        <div>
          <span
            css={css`
              font-size: 13px;
              font-weight: 900;
              color: #000000 !important;
              display: block;

              .theme--dark & {
                color: #cbd5e1 !important;
              }
            `}
          >
            Total Tokens
          </span>
          <span
            css={css`
              font-size: 16px;
              font-weight: 900;
              color: #000000 !important;

              .theme--dark & {
                color: #ffffff !important;
              }
            `}
          >
            {totalTokens.toLocaleString()}
          </span>
        </div>
        <div>
          <span
            css={css`
              font-size: 13px;
              font-weight: 900;
              color: #000000 !important;
              display: block;

              .theme--dark & {
                color: #cbd5e1 !important;
              }
            `}
          >
            Total Cost
          </span>
          <span
            css={css`
              font-size: 16px;
              font-weight: 900;
              color: #000000 !important;

              .theme--dark & {
                color: #ffffff !important;
              }
            `}
          >
            ${totalCost.toFixed(2)}
          </span>
        </div>
        <div>
          <span
            css={css`
              font-size: 13px;
              font-weight: 900;
              color: #000000 !important;
              display: block;

              .theme--dark & {
                color: #cbd5e1 !important;
              }
            `}
          >
            PII Detected
          </span>
          <span
            css={css`
              font-size: 16px;
              font-weight: 900;
              color: ${totalPii > 0 ? "#78350f" : "#064e3b"};

              .theme--dark & {
                color: ${totalPii > 0 ? "#fbbf24" : "#34d399"} !important;
              }
            `}
          >
            {totalPii}
          </span>
        </div>
      </div>
    </div>
  );
}
